import React, { useEffect, useMemo, useState } from 'react';
import { gql, useQuery } from '@apollo/client';

const GET_REQUEST_TAG_OPTIONS = gql`
    query GetRequestTagOptions {
        TagTypeSettings {
            name
            requiredForContentRequests
        }
        TagList(first: 10000) {
            nodes {
                id
                name
                type
            }
        }
    }
`;

type TagOption = {
    id?: string | null;
    name?: string | null;
    type?: string | null;
};

type TagTypeSetting = {
    name?: string | null;
    requiredForContentRequests?: boolean | null;
};

type Props = {
    value: string[];
    onChange: (tagIds: string[]) => void;
    onValidityChange?: (valid: boolean, missingTypes: string[]) => void;
    disabled?: boolean;
};

const RequestTagSelector = ({ value, onChange, onValidityChange, disabled }: Props) => {
    const [showOptional, setShowOptional] = useState(false);
    const { data, loading, error } = useQuery(GET_REQUEST_TAG_OPTIONS);
    const tags = useMemo(
        () => (data?.TagList?.nodes ?? []) as TagOption[],
        [data]
    );
    const settings = useMemo(
        () => (data?.TagTypeSettings ?? []) as TagTypeSetting[],
        [data]
    );

    const groups = useMemo(() => {
        const result = new Map<string, TagOption[]>();
        tags.forEach(tag => {
            if (!tag.id) return;
            const type = tag.type?.trim() || 'Other';
            result.set(type, [...(result.get(type) ?? []), tag]);
        });
        return Array.from(result.entries()).sort(([a], [b]) => a.localeCompare(b));
    }, [tags]);

    const requiredTypes = useMemo(
        () => settings
            .filter(setting => setting.requiredForContentRequests && setting.name)
            .map(setting => setting.name as string),
        [settings]
    );
    const selectedTypes = useMemo(() => new Set(
        tags.filter(tag => tag.id && value.includes(tag.id))
            .map(tag => tag.type?.trim() || 'Other')
    ), [tags, value]);
    const missingTypes = useMemo(
        () => requiredTypes.filter(type => !selectedTypes.has(type)),
        [requiredTypes, selectedTypes]
    );
    const optionalGroups = useMemo(
        () => groups.filter(([type]) => !requiredTypes.includes(type)),
        [groups, requiredTypes]
    );
    const visibleGroups = useMemo(
        () => showOptional ? groups : groups.filter(([type]) => requiredTypes.includes(type)),
        [groups, requiredTypes, showOptional]
    );
    const selectedOptionalCount = useMemo(
        () => optionalGroups.reduce(
            (count, [, options]) => count + options.filter(tag => !!tag.id && value.includes(tag.id)).length,
            0
        ),
        [optionalGroups, value]
    );

    useEffect(() => {
        onValidityChange?.(!loading && !error && missingTypes.length === 0, missingTypes);
    }, [loading, error, missingTypes, onValidityChange]);

    const availableIds = useMemo(
        () => new Set(tags.map(tag => tag.id).filter((id): id is string => !!id)),
        [tags]
    );
    const unavailableCount = value.filter(id => !availableIds.has(id)).length;

    const toggle = (id: string) => {
        if (disabled) return;
        onChange(value.includes(id) ? value.filter(tagId => tagId !== id) : [...value, id]);
    };

    if (loading) {
        return <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>Loading tags…</p>;
    }
    if (error) {
        return <p className="text-sm" style={{ color: 'var(--danger)' }}>Unable to load tag requirements.</p>;
    }

    return (
        <div className="flex flex-col gap-4">
            {visibleGroups.map(([type, options]) => {
                const required = requiredTypes.includes(type);
                const missing = missingTypes.includes(type);
                return (
                    <div key={type}>
                        <div className="flex items-center gap-2 mb-2">
                            <span className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>{type}</span>
                            {required && (
                                <span className="text-xs px-1.5 py-0.5 rounded"
                                    style={{ color: missing ? 'var(--danger)' : 'var(--success)', background: missing ? 'var(--danger-soft)' : 'var(--success-soft)' }}>
                                    Required
                                </span>
                            )}
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {options.map(tag => {
                                const id = tag.id as string;
                                const selected = value.includes(id);
                                return (
                                    <button
                                        key={id}
                                        type="button"
                                        disabled={disabled}
                                        className="px-2.5 py-1.5 rounded-md border text-sm transition-colors"
                                        style={{
                                            borderColor: selected ? 'var(--accent)' : 'var(--border-default)',
                                            background: selected ? 'var(--accent-soft)' : 'transparent',
                                            color: selected ? 'var(--accent)' : 'var(--text-secondary)',
                                            cursor: disabled ? 'default' : 'pointer',
                                        }}
                                        onClick={() => toggle(id)}
                                    >
                                        {tag.name}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                );
            })}
            {groups.length === 0 && (
                <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>No active tags are available.</p>
            )}
            {groups.length > 0 && optionalGroups.length > 0 && (
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        className="btn btn-ghost text-sm"
                        onClick={() => setShowOptional(current => !current)}
                    >
                        {showOptional ? 'Hide optional tags' : 'Show optional tags'}
                    </button>
                    {!showOptional && selectedOptionalCount > 0 && (
                        <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                            {selectedOptionalCount} optional tag{selectedOptionalCount === 1 ? '' : 's'} selected
                        </span>
                    )}
                </div>
            )}
            {groups.length > 0 && visibleGroups.length === 0 && optionalGroups.length > 0 && (
                <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>
                    No tag types are currently required.
                </p>
            )}
            {unavailableCount > 0 && (
                <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                    {unavailableCount} archived or unavailable tag{unavailableCount === 1 ? '' : 's'} will be retained.
                </p>
            )}
            {missingTypes.length > 0 && (
                <p className="text-xs" style={{ color: 'var(--danger)' }}>
                    Select at least one tag for: {missingTypes.join(', ')}.
                </p>
            )}
        </div>
    );
};

export default RequestTagSelector;
